import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Button } from '../Button';

import * as store from '../../reducers/store';
import * as api from '../../api';

import styles from './styles.module.css';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import LoadingBar from '../LoadingBar';

const DEFAULT_SSO_LABEL = 'Login with my portal';

interface Props {
  onSsoSuccess: (
    registration: api.Patient.Patient & {
      sex?: api.Common.Sex;
      authenticationCode: string;
    },
  ) => void;
  onSsoFailure: () => void;
}

const SsoButton: React.FC<Props> = ({ onSsoSuccess, onSsoFailure }) => {
  const params = useParams<{ authenticationCode: string }>();
  const [openedWindow, setOpenedWindow] = useState<Window | null>(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [broadcastChannel, setBroadcastChannel] =
    useState<BroadcastChannel | null>(null);
  const theme = useSelector((state: store.All) => state.theme);
  const authenticationCode: string | null =
    useSelector((store: store.All) => store.authenticationCode) ??
    params.authenticationCode;

  useEffect(() => {
    if (!authenticationCode) return;
    if (!openedWindow) return;
    if (broadcastChannel) return;
    const bc = new BroadcastChannel('sso_patient');
    setBroadcastChannel(bc);
    bc.onmessage = async (event) => {
      if (event.data?.action !== 'closeWindow') return;
      if (event.data.error) {
        return onSsoFailure();
      }
      try {
        const patient = await api.client.Assistant.me();
        onSsoSuccess({
          ...patient,
          sex: undefined,
          authenticationCode,
        });
      } catch (error) {
        console.error(error);
      }
      bc.close();
      setOpenedWindow(null);
      setBroadcastChannel(null);
    };
  }, [
    openedWindow,
    authenticationCode,
    onSsoSuccess,
    broadcastChannel,
    onSsoFailure,
  ]);

  if (!theme?.organizationId) return null;
  if (!theme.ssoEnabled) return null;
  if (!authenticationCode) return;

  const onSsoClicked = () => {
    const url = api.client.common.constructPath(
      `/patient-previsit/auth/reconciliation/organizations/${theme.organizationId}/patients/sso`,
      {
        authenticationCode,
      },
    );
    const baseUrl =
      document.location.hostname === 'localhost'
        ? 'http://localhost:6363'
        : document.location.origin;
    setOpenedWindow(window.open(`${baseUrl}${url}`, '_blank'));
  };

  return (
    <div>
      {!isImageLoaded ? <LoadingBar label={<></>} /> : null}
      <Button
        children={
          <>
            {theme.ssoButtonIconUrl ? (
              <img
                className={styles.SsoButtonIcon}
                src={theme.ssoButtonIconUrl}
                alt="single sign on icon"
                onLoad={() => {
                  setIsImageLoaded(true);
                }}
              />
            ) : null}
            {theme.ssoButtonLabel ? theme.ssoButtonLabel : DEFAULT_SSO_LABEL}
          </>
        }
        invertColors={true}
        className={classNames(
          styles.SsoButton,
          !isImageLoaded ? styles.Hidden : null,
        )}
        type="button"
        onClick={() => onSsoClicked()}
      />
      <div className={styles.SsoDivider}>
        <span className={styles.SsoDividerLabel}>or</span>
      </div>
    </div>
  );
};

export default SsoButton;
